import { bind } from 'bind-event-listener';

import { checkLoomPermission } from './checkLoomPermission';

const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_MS = 1000; // 1 second debounce for visibilitychange

export type CrossJoinFlowHandlerOptions = {
	cloudId: string;
	crossJoinUrl: string;
	onError: (error: Error) => void;
	onListenerRegistered?: () => void;
	onListenerTimedOut?: () => void;
	onListenerTriggered?: () => void;
	onPermissionGranted: () => void;
	onPermissionUnchanged: () => void;
};

export type CrossJoinFlowHandler = {
	cleanup: () => void;
	open: () => void;
};

/**
 * Creates a framework-agnostic handler for the Loom cross-join permission flow.
 *
 * `open()` opens the cross-join URL in a new window and listens for the user
 * returning to the current tab (via `visibilitychange`). When the tab becomes
 * visible again it calls `checkLoomPermission` and invokes the appropriate
 * callback.
 *
 * `cleanup()` tears down the listener and any pending timeouts.
 *
 * The handler is idempotent — calling `open()` multiple times will not create
 * duplicate listeners. A 5-minute timeout automatically cleans up the listener
 * if the user never returns.
 */
export const createCrossJoinFlowHandler = (
	options: CrossJoinFlowHandlerOptions,
): CrossJoinFlowHandler => {
	const {
		cloudId,
		crossJoinUrl,
		onError,
		onListenerRegistered,
		onListenerTimedOut,
		onListenerTriggered,
		onPermissionGranted,
		onPermissionUnchanged,
	} = options;

	let unbindVisibilityChange: (() => void) | null = null;
	let timeoutId: ReturnType<typeof setTimeout> | null = null;
	let debounceId: ReturnType<typeof setTimeout> | null = null;
	let isChecking = false;
	let isCancelled = false;

	const clearTimers = () => {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
			timeoutId = null;
		}
		if (debounceId !== null) {
			clearTimeout(debounceId);
			debounceId = null;
		}
	};

	const removeListener = () => {
		if (unbindVisibilityChange !== null) {
			unbindVisibilityChange();
			unbindVisibilityChange = null;
		}
	};

	const cleanup = () => {
		isCancelled = true;
		clearTimers();
		removeListener();
		isChecking = false;
	};

	const handleVisibilityChange = () => {
		if (document.visibilityState !== 'visible') {
			return;
		}

		// Debounce: ignore rapid visibilitychange events
		if (debounceId !== null) {
			return;
		}

		// Idempotency: ignore if a check is already in progress
		if (isChecking) {
			return;
		}

		debounceId = setTimeout(async () => {
			debounceId = null;
			isChecking = true;
			onListenerTriggered?.();

			try {
				const permitted = await checkLoomPermission(cloudId);

				if (isCancelled) {
					return;
				}

				if (permitted) {
					cleanup();
					onPermissionGranted();
				} else {
					isChecking = false;
					onPermissionUnchanged();
				}
			} catch (error) {
				if (isCancelled) {
					return;
				}
				isChecking = false;
				onError(error instanceof Error ? error : new Error(String(error)));
			}
		}, DEBOUNCE_MS);
	};

	const open = () => {
		// Idempotent: don't create duplicate listeners
		if (unbindVisibilityChange !== null) {
			return;
		}

		window.open(crossJoinUrl, '_blank', 'noopener,noreferrer');

		unbindVisibilityChange = bind(document, {
			type: 'visibilitychange',
			listener: handleVisibilityChange,
		});

		// Auto-cleanup after 5-minute timeout
		timeoutId = setTimeout(() => {
			cleanup();
			onListenerTimedOut?.();
		}, TIMEOUT_MS);

		onListenerRegistered?.();
	};

	return { open, cleanup };
};
